// Copyright 2022 - 2023 Fewcha. All rights reserved.

import { createAction, createAsyncThunk, createReducer } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import axios from "axios";
import { ConfigReducer, emptyTokenType, TokenType } from "features/config/types";
import { getTokens } from "features/tokens/tokensReducer";
import { api } from "services/api";
import { checkAddress, decimalFormat, myToast } from "services/utils";
import { web3 } from "services/web3";
import { checkIsConnected, connect, disconnect, getAccount, getBalance, getNetwork } from "./connect";
import { getReferralCode } from "./referral";
import { defaultWalletReducer, WalletReducer } from "./types";

export const changeLoading = createAction<boolean>("wallet/changeLoading");
export const changeBalance = createAction<string>("wallet/changeBalance");
export const closeSuccessPopup = createAction("wallet/closeSuccessPopup");
export const logout = createAction("wallet/logout");

export const mint = createAsyncThunk(
  "wallet/mint",
  async (
    {
      token,
      address,
      publicKey,
      validNetwork,
    }: {
      token: TokenType;
      address: string;
      publicKey: string;
      validNetwork: boolean;
    },
    { getState, dispatch },
  ) => {
    const { config, wallet } = getState() as { config: ConfigReducer; wallet: WalletReducer };

    const failResponse = { hash: "", isShowPopup: false, currentToken: emptyTokenType };
    const tier = Number(token.tier);

    // referral
    const queryString = window.location.search;
    const parameters = new URLSearchParams(queryString);
    const referralCode = parameters.get("ref");

    // network
    if (!validNetwork) {
      myToast(`Please switch to Aptos ${config.base.NETWORK_NAME} network`, "error");
      return failResponse;
    }

    // whitelist
    if (!(await checkAddress({ address }))) {
      myToast("Account is not in whitelist", "error");
      return failResponse;
    }

    // max whitelist
    try {
      const res = await axios.get(
        `${config.base.NETWORK_URL}v1/accounts/${config.base.COLLECTION_CREATOR}/resource/${config.base.COLLECTION_CREATOR}::fewcha_membership::MembershipBuyTokenCount`,
      );
      const {
        data: {
          count: { handle },
        },
      } = res.data;

      const countMaxRes = await axios.post(`${config.base.NETWORK_URL}v1/tables/${handle}/item`, {
        key_type: "address",
        value_type: "u8",
        key: wallet.address,
      });

      if (countMaxRes.data >= 3) {
        myToast("This address has reached its limit on minting NFT", "error");

        return failResponse;
      }
    } catch (e: any) {
      if (e?.response?.status !== 404) {
        myToast(e.toString(0), "error");
      }
    }

    // sequence_number
    const resGetAccount = await web3.action.aptos.getAccount(address);
    const { status, data: dataAccount } = resGetAccount;
    if (status !== 200) {
      myToast("Account not found", "error", 2000);
      return failResponse;
    }
    const { sequence_number } = dataAccount;

    // simulate txn
    const data = { address, publicKey, level: tier, sequenceNumber: parseInt(sequence_number) };
    const response = await api()
      .post("mainnet/membership/simulate_txn", data)
      .catch((e: any) => e);
    if (response.status !== 200) {
      myToast(
        response?.response?.data?.errorCode?.includes("account_not_found")
          ? "Not enough balance"
          : response?.response?.data?.message ||
              response?.response?.data?.errorCode ||
              "Can not simulate the transaction",
        "error",
      );
      return failResponse;
    }

    const { rawTxn } = response.data;
    const simulateRes = await web3.action.aptos.submitBCSSimulation(Object.values(rawTxn) as any, {
      estimateGasUnitPrice: true,
      estimateMaxGasAmount: true,
      estimatePrioritizedGasUnitPrice: false,
    });

    if (simulateRes.status !== 200) {
      myToast("Can not simulate the transaction", "error");
      return failResponse;
    }

    if (!simulateRes) {
      myToast("Can not simulate the transaction", "error");
      return failResponse;
    }

    if (!simulateRes.data) {
      myToast("Can not simulate the transaction", "error");
      return failResponse;
    }

    const simulate = simulateRes.data[0];
    if (!simulate.success) {
      const mesStatus = simulate.vm_status || "";

      if (mesStatus.includes("ENOT_WHITELISTED" || "WHITELISTED")) {
        myToast("You are not in whitelist", "error");
      } else if (mesStatus.includes("Insufficient NFT balance")) {
        myToast("Insufficient NFT balance", "error");
      } else {
        myToast(mesStatus || "Something error", "error");
      }
      return failResponse;
    }

    // get_raw_txn
    const { gas_unit_price: gasUnitPrice, max_gas_amount: maxGasAmount } = simulateRes.data[0];
    const data2: {
      address: string;
      level: number;
      sequenceNumber: number;
      gasUnitPrice: string;
      maxGasAmount: string;
      referralCode?: string;
    } = {
      address,
      level: tier,
      sequenceNumber: parseInt(sequence_number),
      gasUnitPrice,
      maxGasAmount,
    };
    if (referralCode) {
      data2.referralCode = referralCode;
    }

    const tx = await api()
      .post("mainnet/membership/get_raw_txn", data2)
      .catch((e: any) => e);

    if (tx.status !== 200) {
      myToast("Something wrong. Please try again!", "error");
      return failResponse;
    }

    const { txn, multiAgentTxn, authenticator } = tx.data;

    const result = await (web3.action.aptos as any).signMultiAgentTransaction(Object.values(multiAgentTxn));

    if (result.status !== 200) {
      if (result.status === 401) {
        myToast("Rejected the transaction", "error");
      } else {
        myToast("Something wrong. Please try again!", "error");
      }
      return failResponse;
    }

    // get_signed_txn
    const signature = result.data;

    const signedTx: any = await api().post("mainnet/membership/get_signed_txn", {
      txn,
      authenticator,
      publicKey,
      signature,
    });
    const { signedTxn } = signedTx.data;

    // submit
    const result2 = await web3.action.aptos.submitSignedBCSTransaction(Object.values(signedTxn) as any);
    if (result2.status !== 200) {
      const msgStatus = JSON.parse(result2?.data);
      if (msgStatus && msgStatus?.message?.includes("TRANSACTION_EXPIRED")) {
        myToast("The transaction has expired", "error");
      } else {
        myToast("Something wrong. Please try again!", "error");
      }
      return failResponse;
    }

    // check status
    const txnDetails = await web3.action.aptos.getTransactionByHash(result2.data);
    if (txnDetails.status === 200 && (txnDetails.data as any).success === true) {
      myToast("Mint successfully", "success");
    } else {
      myToast("Insufficient NFT balance", "error");
      dispatch(getTokens());
      return { hash: result2.data, isShowPopup: false, currentToken: null };
    }
    dispatch(getTokens());

    return { hash: result2.data, isShowPopup: true, currentToken: token };
  },
);

const walletReducer = createReducer(defaultWalletReducer, (builder) =>
  builder
    .addCase(connect.pending, (state) => {
      state.loading = true;
    })
    .addCase(connect.rejected, (state) => {
      state.loading = false;
    })
    .addCase(connect.fulfilled, (state, action) => {
      state.loading = false;

      const { address, publicKey, isConnected, networkName, networkUrl, networkType, validNetwork } = action.payload;

      state.address = address;
      state.publicKey = publicKey;
      state.isConnected = isConnected;
      state.networkName = networkName;
      state.networkUrl = networkUrl;
      state.networkType = networkType;
      state.validNetwork = validNetwork;
    })
    .addCase(getAccount.fulfilled, (state, action) => {
      const { address, publicKey, isConnected } = action.payload;

      state.address = address;
      state.publicKey = publicKey;
      state.isConnected = isConnected;
    })
    .addCase(getNetwork.fulfilled, (state, action) => {
      const { networkName, networkType, networkUrl, validNetwork } = action.payload;
      state.networkName = networkName;
      state.networkType = networkType;
      state.networkUrl = networkUrl;
      state.validNetwork = validNetwork;
    })
    .addCase(checkIsConnected.fulfilled, (state, action) => {
      const { isConnected } = action.payload;
      state.isConnected = isConnected;
    })
    .addCase(getBalance.fulfilled, (state, action) => {
      const { balance } = action.payload;
      state.balance = decimalFormat(balance).toString();
    })
    .addCase(disconnect.fulfilled, (state) => {
      state.isConnected = false;
    })
    .addCase(changeLoading, (state, action) => {
      state.loading = action.payload;
    })
    .addCase(mint.pending, (state) => {
      state.minting = true;
    })
    .addCase(mint.fulfilled, (state, action) => {
      state.minting = false;
      if (action.payload.currentToken !== null) {
        state.currentToken = action.payload.currentToken;
        state.mintHash = action.payload.hash;
        state.isShowSuccessPopup = action.payload.isShowPopup;
      }
    })
    .addCase(mint.rejected, (state) => {
      state.minting = false;
    })
    .addCase(closeSuccessPopup, (state) => {
      state.isShowSuccessPopup = false;
      state.currentToken = emptyTokenType;
      state.mintHash = "";
    })
    .addCase(changeBalance, (state, action) => {
      state.balance = action.payload;
    })
    .addCase(logout, (state, action) => {
      state.isConnected = false;
      state.address = "";
      state.publicKey = "";
    })
    .addCase(getReferralCode.fulfilled, (state, action) => {
      state.referralCode = action.payload;
    }),
);
export const selectWallet = (state: RootState) => state.wallet;

export default walletReducer;
